import { useState } from 'react';
import { Button, Dropdown } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { AttachmentListDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { VIEW_FILES_LABEL } from './copy';
import { PdfViewerModal, type PdfSource } from './PdfViewerModal';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3b §4] "VIEW FILES" — EVERY PDF ON ONE ORDER, ONE CLICK FROM THE RECORD.
 *
 *  ⚠️ IT IS ITS OWN COMPONENT SO `pages/ArrangementView/index.tsx` KEEPS ITS CENSUS ROW. That
 *  file is EXEMPT at exactly {Tooltip: 2} and declares no Dropdown and no Modal; putting either
 *  here means its counts do not move at all, and the two new ones are accounted for once, on a
 *  file whose whole subject they are.
 *
 *  ⚠️ `trigger={['click']}` IS NOT OPTIONAL. An antd `Dropdown` with no trigger opens on HOVER —
 *  which shipped once in this repository and was unreachable by keyboard entirely. Every
 *  `Dropdown` here states its trigger.
 *
 *  ⚠️ THE BUTTON IS RENDERED ONLY WHEN THERE IS SOMETHING TO OPEN, WHICH IS HIDE-THEN-SHOW
 *  RATHER THAN A REFUSAL. §4a's rule is not to render an action that will refuse; the count
 *  arrives over the network, so `undefined` means "not yet" and renders nothing, exactly as the
 *  archive slot does one file over. An empty menu is worse than an absent button: it is a
 *  control that opens onto a sentence saying it had nothing to show.
 *
 *  ⚠️ NO PAGINATION, AND THAT IS THE ENDPOINT'S OWN SHAPE. `GET /arrangements/:id/attachments` is
 *  bounded at `ATTACHMENT_MAX_PER_ARRANGEMENT` and returns every row — §7's "bounded AND
 *  page-able where paging is meaningful", where a page of ten files is not a coherent answer.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const AttachmentsMenu = ({ arrangementId }: { arrangementId: string }): React.ReactElement | null => {
  const [viewing, setViewing] = useState<PdfSource | null>(null);

  const attachments = useQuery({
    queryKey: queryKeys.arrangements.attachments(arrangementId),
    queryFn: ({ signal }) => api.get<AttachmentListDto>(`/arrangements/${arrangementId}/attachments`, { signal }),
    staleTime: 30_000,
  });

  const items = attachments.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <>
      <Dropdown
        trigger={['click']}
        menu={{
          items: items.map((attachment) => ({
            key: attachment.id,
            /*
             * ⚠️ TRUNCATED WITH A NATIVE `title`, NEVER A `Tooltip`. [D4.5] measured an antd
             * Tooltip per list row taking one test file from 64s to 119s, and a menu item is a
             * list row. `NotificationBell` uses exactly this pair for exactly this reason.
             * ⚠️ [C3b §6] The server already caps a stored filename, so this truncation is the
             * belt to that braces — it also covers rows filed before that cap existed.
             */
            label: (
              <span className="block max-w-72 truncate" title={attachment.filename}>
                {attachment.filename}
              </span>
            ),
            onClick: () => {
              setViewing({
                path: `/arrangements/${arrangementId}/attachments/${attachment.id}`,
                filename: attachment.filename,
              });
            },
          })),
        }}
      >
        <Button data-testid="view-files">{VIEW_FILES_LABEL(items.length)}</Button>
      </Dropdown>

      <PdfViewerModal
        source={viewing}
        onClose={() => {
          setViewing(null);
        }}
      />
    </>
  );
};
